import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCurrency, formatDateTime, downloadAuthenticated } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import { Search, ChevronLeft, ChevronRight, Download, FileSpreadsheet, FileText, Calendar } from "lucide-react";

const Rides = () => {
    const [data, setData] = useState({ total: 0, items: [] });
    const [status, setStatus] = useState("");
    const [vehicle, setVehicle] = useState("");
    const [q, setQ] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [skip, setSkip] = useState(0);
    const limit = 25;
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState("");

    const buildParams = () => {
        const p = new URLSearchParams();
        if (status) p.set("status", status);
        if (vehicle) p.set("vehicle_type", vehicle);
        if (q) p.set("q", q);
        if (fromDate) p.set("from", fromDate);
        if (toDate) p.set("to", toDate);
        return p;
    };

    const fetchRides = () => {
        setLoading(true);
        const p = buildParams();
        p.set("limit", limit);
        p.set("skip", skip);
        api.get(`/rides?${p.toString()}`)
            .then((res) => setData(res.data))
            .finally(() => setLoading(false));
    };

    useEffect(fetchRides, [status, vehicle, skip, fromDate, toDate]);

    const exportFile = async (format) => {
        setDownloading(format);
        try {
            const p = buildParams();
            p.set("format", format);
            const ts = new Date().toISOString().slice(0, 10);
            await downloadAuthenticated(`/rides/export?${p.toString()}`, `yugo-rides-${ts}.${format}`);
        } catch (e) {
            window.alert("Download failed: " + e.message);
        } finally {
            setDownloading("");
        }
    };

    const setQuickRange = (days) => {
        const to = new Date();
        const from = new Date();
        from.setDate(from.getDate() - days + 1);
        setFromDate(from.toISOString().slice(0, 10));
        setToDate(to.toISOString().slice(0, 10));
        setSkip(0);
    };

    const clearDates = () => { setFromDate(""); setToDate(""); setSkip(0); };

    const page = Math.floor(skip / limit) + 1;
    const totalPages = Math.max(1, Math.ceil(data.total / limit));

    return (
        <div className="space-y-6">
            <PageHeader
                title="All Rides"
                subtitle={`${data.total} rides${fromDate || toDate ? " in range" : ""}`}
                action={
                    <div className="flex gap-2">
                        <button
                            onClick={() => exportFile("csv")}
                            disabled={downloading === "csv"}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-200 hover:bg-slate-50 text-navy-900 text-sm font-semibold disabled:opacity-50"
                            data-testid="rides-export-csv"
                        >
                            <FileText className="w-4 h-4" />
                            {downloading === "csv" ? "…" : "CSV"}
                        </button>
                        <button
                            onClick={() => exportFile("xlsx")}
                            disabled={downloading === "xlsx"}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-mint-500 hover:bg-mint-600 text-white text-sm font-semibold disabled:opacity-50"
                            data-testid="rides-export-xlsx"
                        >
                            <FileSpreadsheet className="w-4 h-4" />
                            {downloading === "xlsx" ? "…" : "Excel"}
                        </button>
                    </div>
                }
            />

            <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
                <div className="flex flex-wrap gap-3 items-center">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && (setSkip(0), fetchRides())}
                            placeholder="Search ride ID / pickup / drop"
                            className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-mint-500/30"
                            data-testid="rides-search-input"
                        />
                    </div>
                    <select value={status} onChange={(e) => { setStatus(e.target.value); setSkip(0); }} className="px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg" data-testid="rides-status-filter">
                        <option value="">All statuses</option>
                        <option value="requested">Requested</option>
                        <option value="assigned">Assigned</option>
                        <option value="in_progress">In progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                    </select>
                    <select value={vehicle} onChange={(e) => { setVehicle(e.target.value); setSkip(0); }} className="px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg" data-testid="rides-vehicle-filter">
                        <option value="">All vehicles</option>
                        {["Auto", "Mini Cab", "Pocket Rocket", "Premium Cab", "Yugo Bizz", "Rental", "Cab XL"].map((v) => (
                            <option key={v} value={v}>{v}</option>
                        ))}
                    </select>
                </div>
                <div className="flex flex-wrap gap-3 items-center">
                    <div className="flex items-center gap-1.5 text-xs text-slate-500 font-semibold uppercase tracking-wider"><Calendar className="w-3.5 h-3.5" /> Date range</div>
                    <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => { setFromDate(e.target.value); setSkip(0); }}
                        className="px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg"
                        data-testid="rides-from-date"
                    />
                    <span className="text-slate-400 text-sm">→</span>
                    <input
                        type="date"
                        value={toDate}
                        onChange={(e) => { setToDate(e.target.value); setSkip(0); }}
                        className="px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg"
                        data-testid="rides-to-date"
                    />
                    <button onClick={() => setQuickRange(1)} className="px-3 py-1.5 text-xs rounded-full border border-slate-200 hover:bg-slate-50 font-semibold" data-testid="rides-quick-today">Today</button>
                    <button onClick={() => setQuickRange(7)} className="px-3 py-1.5 text-xs rounded-full border border-slate-200 hover:bg-slate-50 font-semibold" data-testid="rides-quick-7d">7 days</button>
                    <button onClick={() => setQuickRange(30)} className="px-3 py-1.5 text-xs rounded-full border border-slate-200 hover:bg-slate-50 font-semibold" data-testid="rides-quick-30d">30 days</button>
                    {(fromDate || toDate) && (
                        <button onClick={clearDates} className="px-3 py-1.5 text-xs rounded-full text-slate-500 hover:bg-slate-50 font-semibold" data-testid="rides-clear-dates">Clear</button>
                    )}
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                <th className="px-4 py-3">Ride ID</th>
                                <th className="px-4 py-3">Customer</th>
                                <th className="px-4 py-3">Driver</th>
                                <th className="px-4 py-3">Vehicle</th>
                                <th className="px-4 py-3">Route</th>
                                <th className="px-4 py-3 text-right">Distance</th>
                                <th className="px-4 py-3 text-right">Fare</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Created</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
                            ) : data.items.length === 0 ? (
                                <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No rides match your filters.</td></tr>
                            ) : data.items.map((r) => (
                                <tr key={r.id} className="hover:bg-slate-50 transition-colors" data-testid={`ride-row-${r.id}`}>
                                    <td className="px-4 py-3"><Link to={`/rides/${r.id}`} className="text-mint-600 font-semibold hover:underline">{r.id}</Link></td>
                                    <td className="px-4 py-3">{r.customer?.name || "—"}</td>
                                    <td className="px-4 py-3">{r.driver?.name || "—"}</td>
                                    <td className="px-4 py-3 text-slate-600">{r.vehicle_type}</td>
                                    <td className="px-4 py-3 text-slate-600 max-w-[260px] truncate">{r.pickup_address} → {r.drop_address}</td>
                                    <td className="px-4 py-3 text-right tabular-nums">{r.distance_km} km</td>
                                    <td className="px-4 py-3 text-right font-semibold tabular-nums">{formatCurrency(r.fare)}</td>
                                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDateTime(r.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm">
                    <div className="text-slate-500">Page {page} of {totalPages}</div>
                    <div className="flex gap-2">
                        <button onClick={() => setSkip(Math.max(0, skip - limit))} disabled={skip === 0} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40" data-testid="rides-prev-page"><ChevronLeft className="w-4 h-4" /></button>
                        <button onClick={() => setSkip(skip + limit)} disabled={skip + limit >= data.total} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40" data-testid="rides-next-page"><ChevronRight className="w-4 h-4" /></button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Rides;
